import axios, {
  type AxiosError,
  type AxiosInstance,
  type AxiosRequestConfig,
  type AxiosRequestHeaders,
  type InternalAxiosRequestConfig,
} from "axios";
import { env } from "../config/env";
import { ApiError, type ApiErrorResponse } from "./types";
import {
  buildApiErrorReportContext,
  normalizeApiError,
  normalizeAxiosError,
} from "./error-normalizer";
import { reportToSentry } from "../monitoring/sentry";

type AuthTokenResolver = () => string | null;
type UnauthorizedHandler = (error: ApiError) => void;

let authTokenResolver: AuthTokenResolver | null = null;
let unauthorizedHandler: UnauthorizedHandler | null = null;

export const setAuthTokenResolver = (resolver: AuthTokenResolver | null) => {
  authTokenResolver = resolver;
};

export const setUnauthorizedHandler = (handler: UnauthorizedHandler | null) => {
  unauthorizedHandler = handler;
};

const attachAuthorizationHeader = (
  config: InternalAxiosRequestConfig
): InternalAxiosRequestConfig => {
  if (!config.headers) {
    config.headers = {} as AxiosRequestHeaders;
  }

  const token = authTokenResolver?.();

  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  return config;
};

const notifyUnauthorized = (error: ApiError) => {
  if (error.status === 401) {
    unauthorizedHandler?.(error);
  }
};

const normalizeError = (error: unknown) => {
  let normalizedError: ApiError;

  if (axios.isAxiosError(error)) {
    normalizedError = normalizeAxiosError(
      error as AxiosError<ApiErrorResponse>
    );
    reportToSentry(normalizedError, buildApiErrorReportContext(error));
  } else {
    normalizedError = normalizeApiError(error);
    reportToSentry(normalizedError, {
      mechanism: "api",
      handled: true,
      tags: {
        error_source: "api-client",
      },
    });
  }

  notifyUnauthorized(normalizedError);
  return normalizedError;
};

const createAxiosClient = (): AxiosInstance => {
  const instance = axios.create({
    baseURL: env.apiUrl,
    timeout: env.apiTimeout,
    withCredentials: true,
    headers: {
      "Content-Type": "application/json",
    },
  });

  instance.interceptors.request.use(attachAuthorizationHeader);

  instance.interceptors.response.use(
    (response) => response,
    (error) => Promise.reject(normalizeError(error))
  );

  return instance;
};

export const apiClient = createAxiosClient();

export const apiRequest = async <T = unknown>(
  config: AxiosRequestConfig
): Promise<T> => {
  try {
    const response = await apiClient.request<T>(config);
    return response.data;
  } catch (error) {
    throw normalizeError(error);
  }
};
